/**
 * SITES-TO-VISIT-PANEL.TSX
 * List all the sites for the specified establishment
 */

import SidePanel from "@/components/SidePanel"
import ListItem from "@/components/list-item"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import mapStore from "@/entities/map/model/map.store"
import { faArrowDown, faArrowUp, faLayerGroup, faPencilAlt } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import siteStore from "../model/site.store"
import { AnimatePresence, motion } from "framer-motion"
import SiteChip from "./SiteChip"
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from "@dnd-kit/core"
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { useState } from "react"
import Space from "@/components/space"
import ListButton from "@/components/list-button"
import MapEditModal from "@/entities/map/ui/modal/MapEditModal"
import { Site } from "../model/site.types"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useRisksSpace } from "@/core/risks-space.context"

export default function SitesNav() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { getPath, isReadonly } = useRisksSpace()

  // Selectors
  const _session = useAppSelector((state) => state._session)
  const activeMap = useAppSelector(mapStore.selectors.active)
  const sites = useAppSelector(siteStore.selectors.listForMap)
  const selectedSites = useAppSelector(siteStore.selectors.selected)
  const maps = useAppSelector(mapStore.selectors.list)
  const otherSites = useAppSelector(siteStore.selectors.withoutMap)
  const activeSite = useAppSelector(siteStore.selectors.active)
  const isOtherActive =
    searchParams.get("view") === "other" || !maps.length || (Boolean(activeSite.EstablishmentId) && !activeSite.MapId)

  // State
  const [modal, setModal] = useState<null | "MODAL_MAP_EDIT">(null)

  // DnD sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 180,
        tolerance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  // Change order for the maps / sites on drag and drop
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (isReadonly || !over || active.id === over.id) return

    const activeId = String(active.id)
    const overId = String(over.id)

    // SITES
    if (activeId.startsWith("site:") && overId.startsWith("site:")) {
      const draggedSiteId = activeId.slice(5)
      const targetSiteId = overId.slice(5)

      const currentMap = maps.find((map) => map.id === activeMap?.id)
      if (!currentMap) return

      let baseOrder = currentMap.sitesOrder

      // Fallback si l'ordre n'est pas complet
      if (!baseOrder.includes(draggedSiteId) || !baseOrder.includes(targetSiteId)) {
        baseOrder = sites.filter((site) => site.MapId === currentMap.id).map((site) => String(site.id))
      }

      const oldIndex = baseOrder.indexOf(draggedSiteId)
      const newIndex = baseOrder.indexOf(targetSiteId)
      if (oldIndex < 0 || newIndex < 0) return

      const newSitesOrder = arrayMove(baseOrder, oldIndex, newIndex)
      dispatch(mapStore.actions.edit({ key: "sitesOrder", value: newSitesOrder }))
      dispatch(mapStore.thunks.updateEntity())
    }
  }

  function handleSelectSite(site: Site) {
    navigate(getPath(`/sites/${site.id}`))
  }

  function moveMap(index: number, direction: -1 | 1) {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= maps.length) return

    const reorderedMaps = arrayMove(maps, index, targetIndex)
    dispatch(mapStore.actions.setList(reorderedMaps))
    dispatch(mapStore.thunks.order(reorderedMaps))
  }

  const content = (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      {maps.map((map, mapIndex) => {
        const isMapActive = !isOtherActive && map.id === activeMap?.id
        const visibleSites = isMapActive ? sites.filter((site) => site.MapId === map.id) : []

        return (
          <div key={map.id}>
            <ListItem
              itemId={`map:${map.id}`}
              className="pr-10 _hover"
              onClick={() => {
                dispatch(mapStore.actions.setActive(map))
                dispatch(siteStore.actions.setActive(null))
                navigate(getPath("/sites"))
              }}>
              <div className={!isMapActive ? "grey-t" : ""}>
                {isMapActive && (
                  <FontAwesomeIcon
                    style={{ color: _session.accountColors.active }}
                    icon={faLayerGroup}
                    className="mr-1"
                  />
                )}
                <b>{map.name}</b>
              </div>

              <Space />

              <div className={isReadonly ? "hidden" : "flex items-center"}>
                {maps.length > 1 && (
                  <>
                    <ListButton
                      isSmall
                      isInactive={mapIndex === 0}
                      text="Monter le niveau"
                      tooltipPosition={mapIndex === maps.length - 1 ? "top" : "bottom"}
                      icon={faArrowUp}
                      onClick={() => moveMap(mapIndex, -1)}
                    />
                    <ListButton
                      isSmall
                      isInactive={mapIndex === maps.length - 1}
                      text="Descendre le niveau"
                      tooltipPosition={mapIndex === maps.length - 1 ? "top" : "bottom"}
                      icon={faArrowDown}
                      onClick={() => moveMap(mapIndex, 1)}
                    />
                  </>
                )}
                <ListButton
                  isSmall
                  tooltipPosition="left"
                  text="Mettre à jour le niveau"
                  icon={faPencilAlt}
                  onClick={() => {
                    dispatch(mapStore.actions.setActive(map))
                    setModal("MODAL_MAP_EDIT")
                  }}
                />
              </div>
            </ListItem>

            <AnimatePresence initial={false}>
              {isMapActive && (
                <motion.div
                  key={`map-sites-${map.id}`}
                  initial={{ height: 0, opacity: 0, y: -6 }}
                  animate={{ height: "auto", opacity: 1, y: 0 }}
                  exit={{ height: 0, opacity: 0, y: -6 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                  style={{ overflow: "hidden" }}
                  className="ml-[24px] lightwhite-b">
                  <div>
                    <SortableContext
                      items={visibleSites.map((site) => `site:${site.id}`)}
                      strategy={verticalListSortingStrategy}>
                      {visibleSites.map((site) => (
                        <ListItem
                          isSmall
                          onMouseEnter={() => {
                            if (selectedSites[0] !== site.id) {
                              dispatch(siteStore.actions.select(site.id))
                            }
                          }}
                          onMouseLeave={() => dispatch(siteStore.actions.select([]))}
                          key={site.id}
                          itemId={`site:${site.id}`}
                          isDraggable={!isReadonly}
                          isEditable
                          className="border-none py-10"
                          onClick={() => handleSelectSite(site)}>
                          <div className="flex1">
                            <div className="flex items-center">
                              <SiteChip
                                site={site}
                                onClick={() => {
                                  handleSelectSite(site)
                                }}
                              />
                            </div>
                          </div>
                        </ListItem>
                      ))}

                      <div className="h-20" />
                    </SortableContext>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
      {otherSites.length > 0 && (
        <ListItem isEditable isActive={isOtherActive} onClick={() => navigate(getPath("/sites?view=other"))}>
          <b>Autres lieux de travail</b>
        </ListItem>
      )}
    </DndContext>
  )

  return (
    <>
      {modal === "MODAL_MAP_EDIT" && <MapEditModal onClose={() => setModal(null)} />}

      <SidePanel side="left" defaultOpen fitContent minWidth={280} maxWidth={480}>
        {content}
      </SidePanel>
    </>
  )
}
